import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Globe, MapPin, Package, Plane } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const ShippingDestinationsPage = () => {
  const destinations = {
    primaryCountries: [
      { name: 'India', url: '/shipping-to-india-from-canada', icon: '🇮🇳', popular: true },
      { name: 'United Kingdom', url: '/shipping-canada-to-uk', icon: '🇬🇧', popular: true },
      { name: 'Dubai/UAE', url: '/shipping-canada-to-uae', icon: '🇦🇪', popular: true },
      { name: 'Pakistan', url: '/shipping-canada-to-pakistan', icon: '🇵🇰', popular: false },
      { name: 'USA', url: '/shipping-canada-to-usa', icon: '🇺🇸', popular: false },
      { name: 'Australia', url: '/shipping-canada-to-australia', icon: '🇦🇺', popular: false },
      { name: 'Bangladesh', url: '/shipping-canada-to-bangladesh', icon: '🇧🇩', popular: false },
      { name: 'Germany', url: '/shipping-canada-to-germany', icon: '🇩🇪', popular: false },
      { name: 'France', url: '/shipping-canada-to-france', icon: '🇫🇷', popular: false }
    ],
    canadianCities: [
      { name: 'Toronto', url: '/international-courier-toronto', region: 'Ontario' },
      { name: 'Brampton', url: '/international-shipping-brampton', region: 'Ontario' },
      { name: 'Mississauga', url: '/international-shipping-mississauga', region: 'Ontario' },
      { name: 'Markham', url: '/markham-to-india-courier', region: 'Ontario' },
      { name: 'Vaughan', url: '/vaughan-to-india-courier', region: 'Ontario' },
      { name: 'Richmond Hill', url: '/richmond-hill-to-india-courier', region: 'Ontario' },
      { name: 'Oakville', url: '/oakville-to-india-courier', region: 'Ontario' },
      { name: 'Hamilton', url: '/hamilton-to-india-courier', region: 'Ontario' },
      { name: 'Kitchener', url: '/kitchener-to-india-courier', region: 'Ontario' },
      { name: 'Waterloo', url: '/waterloo-to-india-courier', region: 'Ontario' },
      { name: 'Calgary', url: '/calgary-to-india-courier', region: 'Alberta' },
      { name: 'Surrey', url: '/international-shipping-surrey', region: 'British Columbia' },
      { name: 'Vancouver', url: '/international-shipping-vancouver', region: 'British Columbia' },
      { name: 'Nova Scotia', url: '/international-shipping-nova-scotia', region: 'Atlantic' }
    ],
    shippingServices: [
      { name: 'Cheap Shipping to India', url: '/cheap-shipping-canada-to-india' },
      { name: 'Relocation Shipping', url: '/relocation-shipping-canada-to-india' },
      { name: 'Document Shipping', url: '/documents-to-india-from-canada' },
      { name: 'Suitcase Shipping', url: '/ship-suitcase-canada-to-india' },
      { name: 'Student Shipping', url: '/student-shipping-canada-to-india' },
      { name: 'Excess Baggage', url: '/excess-baggage-shipping-canada-to-india' },
      { name: 'Household Goods', url: '/household-goods-shipping-canada-to-india' },
      { name: '23kg Parcel', url: '/23kg-parcel-canada-to-india' },
      { name: 'Business Shipping', url: '/business-ecommerce-shipping' }
    ],
    resources: [
      { name: 'Shipping Cost Calculator', url: '/shipping-cost-canada-to-india' },
      { name: 'Customs Declaration Form', url: '/customs-declaration' },
      { name: 'Customs Duties Guide', url: '/customs-duties-guide-india' },
      { name: 'Packaging Guide', url: '/packaging-guide-international-shipping' },
      { name: 'Prohibited Items', url: '/prohibited-items-international-shipping' },
      { name: 'Volumetric Weight Calculator', url: '/actual-weight-vs-volumetric-weight' }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Shipping Destinations | International Courier from Canada | Shippzy</title>
        <meta name="description" content="Explore all international shipping destinations from Canada with Shippzy. Ship to India, UK, UAE, and worldwide from major Canadian cities." />
        <link rel="canonical" href="https://shippzy.ca/shipping-destinations" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-24 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Globe className="inline mr-2 h-4 w-4" />
              Ship Worldwide from Canada
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Shipping Destinations
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Shippzy provides reliable international courier services from Canada to destinations worldwide. 
              Choose your destination or Canadian origin city below.
            </p>
          </div>

          {/* International Destinations */}
          <div className="mb-16">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold flex items-center">
                  <Plane className="mr-3 h-6 w-6 text-red-600" />
                  International Destinations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {destinations.primaryCountries.map((country) => (
                    <a
                      key={country.name}
                      href={country.url}
                      className={`p-4 rounded-lg border-2 transition-all hover:shadow-lg hover:-translate-y-1 ${
                        country.popular 
                          ? 'border-red-600 bg-red-50' 
                          : 'border-gray-200 hover:border-red-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{country.icon}</span>
                        <div>
                          <p className="font-bold text-gray-900">{country.name}</p>
                          {country.popular && (
                            <span className="text-xs text-red-600 font-semibold">Popular Destination</span>
                          )}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Canadian Cities */}
          <div className="mb-16">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold flex items-center">
                  <MapPin className="mr-3 h-6 w-6 text-blue-600" />
                  Shipping from Canadian Cities
                </CardTitle>
                <p className="text-gray-600 mt-2">
                  Find shipping services available from your city across Canada. These are service area pages, 
                  not physical office locations.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Ontario Cities */}
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-3">Ontario</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {destinations.canadianCities
                        .filter(city => city.region === 'Ontario')
                        .map((city) => (
                          <a
                            key={city.name}
                            href={city.url}
                            className="p-3 text-center rounded-lg border border-gray-200 hover:border-blue-600 hover:bg-blue-50 transition-all"
                          >
                            <p className="font-semibold text-gray-900">{city.name}</p>
                          </a>
                        ))}
                    </div>
                  </div>

                  {/* British Columbia */}
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-3">British Columbia</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {destinations.canadianCities
                        .filter(city => city.region === 'British Columbia')
                        .map((city) => (
                          <a
                            key={city.name}
                            href={city.url}
                            className="p-3 text-center rounded-lg border border-gray-200 hover:border-blue-600 hover:bg-blue-50 transition-all"
                          >
                            <p className="font-semibold text-gray-900">{city.name}</p>
                          </a>
                        ))}
                    </div>
                  </div>

                  {/* Other Regions */}
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-3">Other Regions</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {destinations.canadianCities
                        .filter(city => city.region !== 'Ontario' && city.region !== 'British Columbia')
                        .map((city) => (
                          <a
                            key={city.name}
                            href={city.url}
                            className="p-3 text-center rounded-lg border border-gray-200 hover:border-blue-600 hover:bg-blue-50 transition-all"
                          >
                            <p className="font-semibold text-gray-900">{city.name}</p>
                            <p className="text-xs text-gray-600">{city.region}</p>
                          </a>
                        ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Shipping Services */}
          <div className="mb-16">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold flex items-center">
                  <Package className="mr-3 h-6 w-6 text-green-600" />
                  Specialized Shipping Services
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {destinations.shippingServices.map((service) => (
                    <a
                      key={service.name}
                      href={service.url}
                      className="p-4 rounded-lg border border-gray-200 hover:border-green-600 hover:bg-green-50 transition-all"
                    >
                      <p className="font-semibold text-gray-900">{service.name}</p>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resources */}
          <div className="mb-16">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Shipping Tools & Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {destinations.resources.map((resource) => (
                    <a
                      key={resource.name}
                      href={resource.url}
                      className="p-4 rounded-lg border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-all"
                    >
                      <p className="font-semibold text-gray-900">{resource.name}</p>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-red-600 to-orange-500 text-white p-8 rounded-xl">
            <h2 className="text-3xl font-bold mb-4">Ready to Ship?</h2>
            <p className="text-lg mb-6">
              Get an instant quote or schedule a pickup from your location.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-red-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
              >
                Get Quote
              </a>
              <a
                href="/schedule-pickup"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-red-600 transition-colors"
              >
                Schedule Pickup
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingDestinationsPage;
